<script setup lang="ts">
/**
 * The media composer's image editor, as WhatsApp Web's (measured in "Apenas Leo" on 2026-10-29):
 * close, undo and redo on the left; crop and rotate, filter, draw, text, shapes, blur, emoji,
 * sticker, the photo's quality, copy and download on the right; each tool's options at the
 * bottom, where the caption was. The edit lives in the session (imageEdit.ts); the composer
 * sends the photo drawn from it (imageRender.ts).
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from "vue";
import "@fontsource/noto-serif/400.css";
import "@fontsource/norican/400.css";
import "@fontsource/gochi-hand/400.css";
import "@fontsource/oswald/700.css";
import Icon from "./Icon.vue";
import EditorColors from "./EditorColors.vue";
import EmojiPicker from "./EmojiPicker.vue";
import { formatFileSize } from "@desfecha/contracts";
import { locale, t } from "../i18n";
import { api, apiUrl } from "../api/client";
import { inbox } from "../composables/inbox";
import { toast } from "../composables/feedback";
import { useEscapeLayer } from "../composables/escape";
import { vTooltip } from "../directives/tooltip";
import {
  BLUR_SIZE,
  BRUSH_SIZES,
  DRAW_COLOR,
  FILTERS,
  FONTS,
  INK,
  SHAPE_WIDTH,
  TEXT_SIZE,
  blankEdit,
  cloneEdit,
  cropOf,
  displayAngle,
  dragCrop,
  fitScale,
  fontOf,
  fromTurned,
  hasHd,
  isPristine,
  itemAt,
  newItemId,
  outputSize,
  redo as redoEdit,
  remember,
  textLook,
  toTurned,
  turn,
  turnCrop,
  turnedSize,
  undo as undoEdit,
  type BlurMode,
  type EditState,
  type FilterId,
  type Handle,
  type History,
  type Item,
  type Mark,
  type Point,
  type Quality,
  type Rect,
  type Rotation,
  type ShapeKind,
  type TextItem,
} from "../composables/imageEdit";
import {
  exportEdit,
  loadStickers,
  renderEdit,
  sizeText,
  type PhotoSource,
} from "../composables/imageRender";

export interface ImageSession {
  edit: EditState;
  history: History;
  quality: Quality;
}

type Tool = "crop" | "filter" | "draw" | "text" | "shape" | "blur";
type Menu =
  | "shapes"
  | "emoji"
  | "sticker"
  | "hd"
  | "font"
  | "fill"
  | "stroke"
  | "blurMode";

const props = defineProps<{ file: Blob; name: string }>();
const session = defineModel<ImageSession>({ required: true });
const emit = defineEmits<{
  close: [];
  busy: [busy: boolean];
  thumb: [url: string];
}>();

const source = shallowRef<PhotoSource | null>(null);
const stage = ref<HTMLElement>();
const canvas = ref<HTMLCanvasElement>();
const box = reactive({ w: 0, h: 0 });
const tool = ref<Tool | null>(null);
const menu = ref<Menu | null>(null);
const selected = ref<string | null>(null);
const editing = ref<string | null>(null);
const live = shallowRef<Mark | null>(null);
const draft = ref<{ rotation: Rotation; crop: Rect } | null>(null);
const drawColor = ref<string | null>(DRAW_COLOR);
const brush = ref(0);
const blurMode = ref<BlurMode>("pixelate");
const blurSize = ref<number>(BLUR_SIZE.default);
const stickers = shallowRef(new Map<string, CanvasImageSource>());
const recent = ref<{ url: string; key: string }[] | null>(null);
const standardSize = ref<number | null>(null);
const textInput = ref<HTMLElement>();
const stickerInput = ref<HTMLInputElement>();
const ownUrls: string[] = [];

const edit = computed(() => session.value.edit);
const W = computed(() => source.value?.w ?? 1);
const H = computed(() => source.value?.h ?? 1);
/** Source pixels per screen pixel: new strokes and items are born at the size seen. */
const unit = computed(() => (fit.value > 0 ? 1 / fit.value : 1));
const rotation = computed<Rotation>(() =>
  draft.value ? draft.value.rotation : edit.value.rotation,
);
const shownCrop = computed<Rect>(() => {
  if (draft.value) {
    const s = turnedSize(W.value, H.value, draft.value.rotation);
    return { x: 0, y: 0, w: s.w, h: s.h };
  }
  return cropOf(edit.value, W.value, H.value);
});
const fit = computed(() =>
  fitScale(
    shownCrop.value.w,
    shownCrop.value.h,
    box.w - 64,
    box.h - (draft.value ? 64 : 40),
  ),
);
const photoStyle = computed(() => ({
  width: shownCrop.value.w * fit.value + "px",
  height: shownCrop.value.h * fit.value + "px",
}));
const view = computed<EditState>(() => {
  const base = draft.value
    ? { ...edit.value, rotation: draft.value.rotation, crop: null }
    : edit.value;
  return live.value ? { ...base, marks: [...base.marks, live.value] } : base;
});
const current = computed<Item | null>(
  () => edit.value.items.find((i) => i.id === selected.value) ?? null,
);
const busy = computed(() => tool.value !== null || current.value !== null);
const canUndo = computed(() => session.value.history.past.length > 0);
const canRedo = computed(() => session.value.history.future.length > 0);
const outputCrop = computed(() => cropOf(edit.value, W.value, H.value));
const hd = computed(() => hasHd(outputCrop.value.w, outputCrop.value.h));
const standardDims = computed(() =>
  outputSize(outputCrop.value.w, outputCrop.value.h, "standard"),
);

watch(busy, (b) => emit("busy", b), { immediate: true });

// ── the photo and the screen ───────────────────────────────────────────────────────────────────

watch(
  () => props.file,
  async (file) => {
    const bitmap = await createImageBitmap(file);
    source.value = { image: bitmap, w: bitmap.width, h: bitmap.height };
  },
  { immediate: true },
);

let frame = 0;
function schedule() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    const c = canvas.value,
      src = source.value;
    if (!c || !src || !fit.value) return;
    const dpr = window.devicePixelRatio || 1;
    renderEdit(c, src, view.value, {
      scale: fit.value * dpr,
      skip: editing.value,
      whole: Boolean(draft.value),
      stickers: stickers.value,
    });
  });
}
watch([view, fit, editing, source, stickers], schedule, { deep: true });

let observer: ResizeObserver | null = null;
onMounted(() => {
  observer = new ResizeObserver(([entry]) => {
    box.w = entry!.contentRect.width;
    box.h = entry!.contentRect.height;
  });
  if (stage.value) observer.observe(stage.value);
  void document.fonts?.ready.then(schedule);
});
onBeforeUnmount(() => {
  observer?.disconnect();
  if (frame) cancelAnimationFrame(frame);
  const src = source.value?.image;
  if (src instanceof ImageBitmap) src.close();
  ownUrls.forEach((u) => URL.revokeObjectURL(u));
});

let thumbTimer = 0;
function scheduleThumb() {
  clearTimeout(thumbTimer);
  thumbTimer = window.setTimeout(() => {
    const src = source.value;
    if (!src) return;
    const crop = cropOf(edit.value, src.w, src.h);
    const c = document.createElement("canvas");
    renderEdit(c, src, edit.value, {
      scale: 120 / Math.max(crop.w, crop.h),
      stickers: stickers.value,
    });
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      ownUrls.push(url);
      emit("thumb", url);
    }, "image/jpeg");
  }, 250);
}

// ── history ───────────────────────────────────────────────────────────────────────────────────

/** A finished change: the state before it goes to the history. */
function commit(next: EditState, before: EditState = edit.value) {
  session.value = {
    ...session.value,
    edit: next,
    history: remember(session.value.history, before),
  };
  scheduleThumb();
}
/** A change still under the pointer: shown, not yet in the history. */
function preview(next: EditState) {
  session.value = { ...session.value, edit: next };
}
function change(fn: (s: EditState) => void) {
  const next = cloneEdit(edit.value);
  fn(next);
  commit(next);
}
function undo() {
  finishText();
  const r = undoEdit(session.value.history, edit.value);
  if (!r) return;
  session.value = { ...session.value, edit: r.state, history: r.history };
  if (!r.state.items.some((i) => i.id === selected.value))
    selected.value = null;
  scheduleThumb();
}
function redo() {
  finishText();
  const r = redoEdit(session.value.history, edit.value);
  if (!r) return;
  session.value = { ...session.value, edit: r.state, history: r.history };
  scheduleThumb();
}

// ── pointer ───────────────────────────────────────────────────────────────────────────────────

function pointAt(e: { clientX: number; clientY: number }): {
  turned: Point;
  source: Point;
} {
  const r = canvas.value!.getBoundingClientRect();
  const turned = {
    x: shownCrop.value.x + (e.clientX - r.left) / fit.value,
    y: shownCrop.value.y + (e.clientY - r.top) / fit.value,
  };
  return {
    turned,
    source: fromTurned(turned, W.value, H.value, rotation.value),
  };
}

/** The centre of what is shown, in source pixels: where new items land. */
function centre(): Point {
  const c = shownCrop.value;
  return fromTurned(
    { x: c.x + c.w / 2, y: c.y + c.h / 2 },
    W.value,
    H.value,
    rotation.value,
  );
}

let drag: {
  kind: "mark" | "move" | "scale" | "side" | "rotate";
  before: EditState;
  start: Point;
  item?: Item;
  handle?: Handle;
} | null = null;

function onPhotoDown(e: PointerEvent) {
  if (draft.value || !source.value) return;
  menu.value = null;
  const p = pointAt(e);
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  if (tool.value === "draw" || tool.value === "blur") {
    live.value =
      tool.value === "draw"
        ? {
            kind: "draw",
            color: drawColor.value ?? DRAW_COLOR,
            size: BRUSH_SIZES[brush.value]! * unit.value,
            points: [p.source.x, p.source.y],
          }
        : {
            kind: "blur",
            mode: blurMode.value,
            size: blurSize.value * 0.8 * unit.value,
            points: [p.source.x, p.source.y],
          };
    drag = { kind: "mark", before: edit.value, start: p.source };
    return;
  }
  const hit = itemAt(
    edit.value.items,
    p.turned,
    W.value,
    H.value,
    rotation.value,
    6 / fit.value,
  );
  if (hit) {
    if (editing.value && editing.value !== hit.id) finishText();
    selected.value = hit.id;
    tool.value =
      hit.kind === "text" ? "text" : hit.kind === "shape" ? "shape" : null;
    drag = { kind: "move", before: edit.value, start: p.source, item: hit };
    return;
  }
  finishText();
  selected.value = null;
  tool.value = null;
}

/** A double click on a text types in it again. */
function onPhotoDbl(e: MouseEvent) {
  if (draft.value || tool.value === "draw" || tool.value === "blur") return;
  const hit = itemAt(
    edit.value.items,
    pointAt(e).turned,
    W.value,
    H.value,
    rotation.value,
    6 / fit.value,
  );
  if (hit?.kind === "text") void startText(hit.id);
}

function onPhotoMove(e: PointerEvent) {
  if (!drag) return;
  const p = pointAt(e);
  if (drag.kind === "mark" && live.value) {
    const pts = live.value.points;
    const dx = p.source.x - pts.at(-2)!,
      dy = p.source.y - pts.at(-1)!;
    if (dx * dx + dy * dy < (1.5 * unit.value) ** 2) return;
    live.value = { ...live.value, points: [...pts, p.source.x, p.source.y] };
    return;
  }
  if (drag.kind === "move" && drag.item) {
    const next = cloneEdit(drag.before);
    const item = next.items.find((i) => i.id === drag!.item!.id);
    if (!item) return;
    item.x = drag.item.x + (p.source.x - drag.start.x);
    item.y = drag.item.y + (p.source.y - drag.start.y);
    preview(next);
  }
}

function onPhotoUp() {
  if (!drag) return;
  if (drag.kind === "mark" && live.value) {
    const mark = live.value;
    live.value = null;
    const next = cloneEdit(drag.before);
    next.marks.push(mark);
    commit(next, drag.before);
  } else if (drag.kind === "move") {
    if (JSON.stringify(drag.before) !== JSON.stringify(edit.value))
      commit(edit.value, drag.before);
  }
  drag = null;
}

// ── items: frame, handles, text ───────────────────────────────────────────────────────────────

function stagePoint(item: Item) {
  const c = toTurned(item, W.value, H.value, rotation.value);
  return {
    x: (c.x - shownCrop.value.x) * fit.value,
    y: (c.y - shownCrop.value.y) * fit.value,
  };
}

const frameStyle = computed(() => {
  const item = current.value;
  if (!item || draft.value) return null;
  const at = stagePoint(item);
  const flat =
    item.kind === "shape" && (item.shape === "line" || item.shape === "arrow");
  return {
    left: at.x + "px",
    top: at.y + "px",
    width: item.w * fit.value + 12 + "px",
    height: (flat ? 0 : item.h * fit.value) + 12 + "px",
    transform: `translate(-50%, -50%) rotate(${displayAngle(item, rotation.value)}deg)`,
  };
});
const handles = computed<Handle[]>(() => {
  const item = current.value;
  if (!item) return [];
  if (
    item.kind === "shape" &&
    (item.shape === "line" || item.shape === "arrow")
  )
    return ["w", "e"];
  if (item.kind === "shape")
    return ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
  return ["nw", "ne", "se", "sw"];
});

function onHandleDown(e: PointerEvent, handle: Handle | "rotate") {
  const item = current.value;
  if (!item) return;
  e.stopPropagation();
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  const p = pointAt(e);
  drag = {
    kind:
      handle === "rotate"
        ? "rotate"
        : handle.length === 2 || item.kind !== "shape"
          ? "scale"
          : "side",
    before: edit.value,
    start: p.turned,
    item: JSON.parse(JSON.stringify(item)) as Item,
    ...(handle !== "rotate" ? { handle } : {}),
  };
}

function onHandleMove(e: PointerEvent) {
  if (!drag?.item || drag.kind === "mark" || drag.kind === "move") return;
  const p = pointAt(e).turned;
  const it = drag.item;
  const c = toTurned(it, W.value, H.value, rotation.value);
  const next = cloneEdit(drag.before);
  const item = next.items.find((i) => i.id === it.id);
  if (!item) return;
  if (drag.kind === "rotate") {
    const deg = (Math.atan2(p.y - c.y, p.x - c.x) * 180) / Math.PI + 90;
    item.angle = Math.round(deg - rotation.value);
    if (Math.abs(((item.angle % 90) + 90) % 90) < 4)
      item.angle = Math.round(item.angle / 90) * 90;
  } else if (drag.kind === "scale") {
    const d0 = Math.hypot(drag.start.x - c.x, drag.start.y - c.y) || 1;
    const k = Math.max(0.15, Math.hypot(p.x - c.x, p.y - c.y) / d0);
    item.w = it.w * k;
    item.h = it.h * k;
    if (item.kind === "text" && it.kind === "text") item.size = it.size * k;
  } else {
    // A side handle stretches the shape along its own axis, around its centre.
    const a = (-displayAngle(it, rotation.value) * Math.PI) / 180;
    const local = (q: Point) => ({
      x: (q.x - c.x) * Math.cos(a) - (q.y - c.y) * Math.sin(a),
      y: (q.x - c.x) * Math.sin(a) + (q.y - c.y) * Math.cos(a),
    });
    const now = local(p);
    const h = drag.handle!;
    if (h === "e" || h === "w")
      item.w = Math.max(8 * unit.value, Math.abs(now.x) * 2);
    if (h === "n" || h === "s")
      item.h = Math.max(8 * unit.value, Math.abs(now.y) * 2);
  }
  preview(next);
}

function onHandleUp() {
  if (drag && drag.kind !== "mark" && drag.kind !== "move") {
    if (JSON.stringify(drag.before) !== JSON.stringify(edit.value))
      commit(edit.value, drag.before);
    drag = null;
  }
}

function addItem(item: Item) {
  finishText();
  change((s) => s.items.push(item));
  selected.value = item.id;
}

function removeSelected() {
  const id = selected.value;
  if (!id) return;
  if (editing.value === id) editing.value = null;
  change((s) => (s.items = s.items.filter((i) => i.id !== id)));
  selected.value = null;
  tool.value = null;
}

function updateItem(fn: (item: Item) => void) {
  const id = selected.value;
  if (!id) return;
  change((s) => {
    const item = s.items.find((i) => i.id === id);
    if (!item) return;
    fn(item);
    if (item.kind === "text") sizeText(item);
  });
}

const editingItem = computed(
  () =>
    edit.value.items.find((i) => i.id === editing.value) as
      TextItem | undefined,
);
const textStyle = computed(() => {
  const item = editingItem.value;
  if (!item) return null;
  const at = stagePoint(item);
  const font = fontOf(item.font);
  const look = textLook(item.color, item.background);
  const px = item.size * fit.value;
  return {
    left: at.x + "px",
    top: at.y + "px",
    transform: `translate(-50%, -50%) rotate(${displayAngle(item, rotation.value)}deg)`,
    font: `${font.weight} ${px}px/${px * (25 / 17)}px ${font.family}`,
    color: look.fg,
    background: look.bg ?? "transparent",
    padding: `${px / 17}px ${(px * 9) / 17}px`,
    borderRadius: `${(px * 6) / 17}px`,
    textAlign: item.align,
  };
});

let textBefore: EditState | null = null;
async function startText(id: string, before: EditState = edit.value) {
  textBefore = before;
  editing.value = id;
  selected.value = id;
  tool.value = "text";
  await nextTick();
  const el = textInput.value;
  if (!el) return;
  el.innerText = editingItem.value?.text ?? "";
  el.focus();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function typing() {
  const el = textInput.value,
    id = editing.value;
  if (!el || !id) return;
  const next = cloneEdit(edit.value);
  const item = next.items.find((i) => i.id === id);
  if (item?.kind !== "text") return;
  item.text = el.innerText.replace(/\n$/, "");
  sizeText(item);
  preview(next);
}

function finishText() {
  const id = editing.value;
  if (!id) return;
  editing.value = null;
  const before = textBefore ?? edit.value;
  textBefore = null;
  const item = edit.value.items.find((i) => i.id === id);
  const next = cloneEdit(edit.value);
  if (item?.kind === "text" && !item.text.trim()) {
    next.items = next.items.filter((i) => i.id !== id);
    if (selected.value === id) selected.value = null;
  }
  if (JSON.stringify(before) !== JSON.stringify(next)) commit(next, before);
  else preview(next);
}

function newText() {
  finishText();
  const at = centre();
  const item = sizeText<TextItem>({
    kind: "text",
    id: newItemId(),
    text: "",
    color: INK,
    font: "sans",
    align: "center",
    background: true,
    size: TEXT_SIZE * unit.value,
    x: at.x,
    y: at.y,
    w: 0,
    h: 0,
    angle: -rotation.value,
  });
  // The empty box goes in without a history step; typing it is the step.
  const before = edit.value;
  preview({ ...cloneEdit(edit.value), items: [...edit.value.items, item] });
  void startText(item.id, before);
}

// ── tools ──────────────────────────────────────────────────────────────────────────────────────

function pickTool(next: Tool) {
  finishText();
  menu.value = null;
  if (next === "text") return newText();
  if (next === "shape") {
    menu.value = menu.value === "shapes" ? null : "shapes";
    return;
  }
  selected.value = null;
  if (tool.value === "crop" && next !== "crop") applyCrop();
  if (next === "crop") {
    if (tool.value === "crop") return applyCrop();
    draft.value = {
      rotation: edit.value.rotation,
      crop: cropOf(edit.value, W.value, H.value),
    };
  }
  tool.value = tool.value === next ? null : next;
}

function addShape(shape: ShapeKind) {
  menu.value = null;
  const at = centre();
  const flat = shape === "line" || shape === "arrow";
  const size = 96 * unit.value;
  addItem({
    kind: "shape",
    id: newItemId(),
    shape,
    stroke: INK,
    fill: null,
    width: SHAPE_WIDTH.default * unit.value,
    x: at.x,
    y: at.y,
    w: flat ? size * 1.25 : size,
    h: flat ? 0 : size,
    angle: (flat ? -45 : 0) - rotation.value,
  });
  tool.value = "shape";
}

function addEmoji(emoji: string, kind: "text" | "sticker" | "image") {
  if (kind !== "text") return;
  menu.value = null;
  const at = centre();
  const size = 64 * unit.value;
  addItem({
    kind: "emoji",
    id: newItemId(),
    emoji,
    x: at.x,
    y: at.y,
    w: size,
    h: size,
    angle: -rotation.value,
  });
  tool.value = null;
}

async function addSticker(url: string) {
  menu.value = null;
  const at = centre();
  const size = 120 * unit.value;
  const item: Item = {
    kind: "sticker",
    id: newItemId(),
    url,
    x: at.x,
    y: at.y,
    w: size,
    h: size,
    angle: -rotation.value,
  };
  stickers.value = await loadStickers(
    { ...edit.value, items: [item] },
    new Map(stickers.value),
  );
  addItem(item);
  tool.value = null;
}

async function openStickers() {
  finishText();
  menu.value = menu.value === "sticker" ? null : "sticker";
  if (menu.value !== "sticker" || recent.value || !inbox.slug) return;
  recent.value = [];
  try {
    const { stickers: list } = await api<{
      stickers: Array<{ messageId: string; url: string }>;
    }>(`/v1/c/${inbox.slug}/stickers`);
    // Fetched as blobs so the edited photo can be drawn from them.
    const loaded = await Promise.all(
      list.map(async (s) => {
        try {
          const res = await fetch(apiUrl(s.url), { credentials: "include" });
          if (!res.ok) return null;
          const url = URL.createObjectURL(await res.blob());
          ownUrls.push(url);
          return { url, key: s.messageId };
        } catch {
          return null;
        }
      }),
    );
    recent.value = loaded.filter(
      (s): s is { url: string; key: string } => s !== null,
    );
  } catch {
    recent.value = [];
  }
}

function stickerFile(e: Event) {
  const el = e.target as HTMLInputElement;
  const file = el.files?.[0];
  el.value = "";
  if (!file) return;
  const url = URL.createObjectURL(file);
  ownUrls.push(url);
  void addSticker(url);
}

function setFilter(f: FilterId) {
  if (edit.value.filter === f) return;
  change((s) => (s.filter = f));
}

function clearBlur() {
  if (!edit.value.marks.some((m) => m.kind === "blur")) return;
  change((s) => (s.marks = s.marks.filter((m) => m.kind !== "blur")));
}

// ── crop ──────────────────────────────────────────────────────────────────────────────────────

const cropBox = computed(() => {
  if (!draft.value) return null;
  const c = draft.value.crop;
  return {
    left: c.x * fit.value,
    top: c.y * fit.value,
    width: c.w * fit.value,
    height: c.h * fit.value,
  };
});
/** The photo outside the crop, darkened, as four bands around it. */
const dims = computed(() => {
  const c = cropBox.value;
  if (!c) return [];
  const px = (v: number) => v + "px";
  return [
    { left: "0", top: "0", right: "0", height: px(c.top) },
    { left: "0", top: px(c.top + c.height), right: "0", bottom: "0" },
    { left: "0", top: px(c.top), width: px(c.left), height: px(c.height) },
    {
      left: px(c.left + c.width),
      top: px(c.top),
      right: "0",
      height: px(c.height),
    },
  ];
});
const draftChanged = computed(() => {
  if (!draft.value) return false;
  const whole = turnedSize(W.value, H.value, draft.value.rotation);
  const c = draft.value.crop;
  return (
    draft.value.rotation !== 0 ||
    c.x > 0.5 ||
    c.y > 0.5 ||
    Math.abs(c.w - whole.w) > 0.5 ||
    Math.abs(c.h - whole.h) > 0.5
  );
});

function rotate(by: 90 | -90) {
  if (!draft.value) return;
  draft.value = {
    rotation: turn(draft.value.rotation, by),
    crop: turnCrop(
      draft.value.crop,
      W.value,
      H.value,
      draft.value.rotation,
      by,
    ),
  };
}

function resetCrop() {
  draft.value = {
    rotation: 0,
    crop: { x: 0, y: 0, w: W.value, h: H.value },
  };
}

let cropDrag: { handle: Handle | "move"; start: Point; from: Rect } | null =
  null;
function onCropDown(e: PointerEvent, handle: Handle | "move") {
  if (!draft.value) return;
  e.stopPropagation();
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  cropDrag = {
    handle,
    start: { x: e.clientX, y: e.clientY },
    from: { ...draft.value.crop },
  };
}
function onCropMove(e: PointerEvent) {
  if (!cropDrag || !draft.value) return;
  const bounds = turnedSize(W.value, H.value, draft.value.rotation);
  draft.value = {
    ...draft.value,
    crop: dragCrop(
      cropDrag.from,
      cropDrag.handle,
      (e.clientX - cropDrag.start.x) / fit.value,
      (e.clientY - cropDrag.start.y) / fit.value,
      bounds,
      32 * unit.value,
    ),
  };
}
function onCropUp() {
  cropDrag = null;
}

function applyCrop() {
  const d = draft.value;
  if (!d) return;
  draft.value = null;
  tool.value = null;
  const whole = turnedSize(W.value, H.value, d.rotation);
  const full =
    d.crop.x < 0.5 &&
    d.crop.y < 0.5 &&
    Math.abs(d.crop.w - whole.w) < 0.5 &&
    Math.abs(d.crop.h - whole.h) < 0.5;
  const crop = full ? null : d.crop;
  if (
    d.rotation === edit.value.rotation &&
    JSON.stringify(crop) === JSON.stringify(edit.value.crop)
  )
    return;
  change((s) => {
    s.rotation = d.rotation;
    s.crop = crop;
  });
}

// ── quality, copy and download ────────────────────────────────────────────────────────────────

async function openQuality() {
  if (!hd.value) return;
  menu.value = menu.value === "hd" ? null : "hd";
  if (menu.value !== "hd") return;
  standardSize.value = null;
  try {
    standardSize.value = (
      await exportEdit(props.file, edit.value, "standard")
    ).blob.size;
  } catch {
    standardSize.value = null;
  }
}

function setQuality(q: Quality) {
  session.value = { ...session.value, quality: q };
  menu.value = null;
}

async function copy() {
  finishText();
  try {
    const { blob } = await exportEdit(
      props.file,
      edit.value,
      session.value.quality,
      "image/png",
    );
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
    toast(t("editor.copied"));
  } catch {
    toast(t("editor.copy_failed"));
  }
}

async function download() {
  finishText();
  try {
    const pristine = isPristine(edit.value) && session.value.quality === "hd";
    const blob = pristine
      ? props.file
      : (await exportEdit(props.file, edit.value, session.value.quality)).blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = pristine
      ? props.name
      : props.name.replace(/\.[^.]+$/, "") + ".jpg";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    toast(t("editor.export_failed"));
  }
}

const brushSizes = BRUSH_SIZES.map((_, i) => i);
const shapeKinds: Array<{ id: ShapeKind; icon: string }> = [
  { id: "rect", icon: "wa-square" },
  { id: "circle", icon: "wa-circle" },
  { id: "line", icon: "wa-line" },
  { id: "arrow", icon: "wa-arrow" },
];
const tools: Array<{
  id: Tool | "emoji" | "sticker";
  icon: string;
  label: Parameters<typeof t>[0];
}> = [
  { id: "crop", icon: "wa-crop-rotate", label: "editor.crop" },
  { id: "filter", icon: "wa-wand", label: "editor.filter" },
  { id: "draw", icon: "wa-pencil", label: "editor.draw" },
  { id: "text", icon: "wa-text", label: "editor.text" },
  { id: "shape", icon: "wa-square", label: "editor.shapes" },
  { id: "blur", icon: "wa-blur", label: "editor.blur" },
  { id: "emoji", icon: "wa-mood", label: "editor.emoji" },
  { id: "sticker", icon: "wa-sticker", label: "editor.sticker" },
];
function onTool(id: Tool | "emoji" | "sticker") {
  if (id === "emoji") {
    finishText();
    menu.value = menu.value === "emoji" ? null : "emoji";
    return;
  }
  if (id === "sticker") return void openStickers();
  pickTool(id);
}
const toolActive = (id: string) =>
  id === "emoji" || id === "sticker"
    ? menu.value === id
    : id === "shape"
      ? menu.value === "shapes" || tool.value === "shape"
      : tool.value === id;

/** Filter thumbnails: the photo as it is shown, small, with each filter. */
const thumbs = ref<Record<string, string>>({});
watch(
  () =>
    [
      tool.value === "filter",
      source.value,
      edit.value.rotation,
      edit.value.crop,
    ] as const,
  ([open, src]) => {
    if (!open || !src) return;
    const crop = cropOf(edit.value, src.w, src.h);
    const scale = 96 / Math.min(crop.w, crop.h);
    const out: Record<string, string> = {};
    for (const f of FILTERS) {
      const c = document.createElement("canvas");
      renderEdit(
        c,
        src,
        {
          ...blankEdit(),
          rotation: edit.value.rotation,
          crop: edit.value.crop,
          filter: f,
        },
        { scale, photoOnly: true },
      );
      out[f] = c.toDataURL("image/jpeg", 0.8);
    }
    thumbs.value = out;
  },
);

watch(
  () => (current.value?.kind === "text" ? current.value.font : null),
  (font) => {
    if (font)
      void document.fonts?.load(`32px ${fontOf(font).family}`).then(() => {
        // Measured again once the face arrives, so the box fits the letters drawn.
        const item = current.value;
        if (item?.kind === "text")
          preview({
            ...cloneEdit(edit.value),
            items: edit.value.items.map((i) =>
              i.id === item.id
                ? sizeText(JSON.parse(JSON.stringify(i)) as TextItem)
                : i,
            ),
          });
        schedule();
      });
  },
);

// Escape closes what is open, the last first, as WhatsApp Web.
useEscapeLayer(menu, () => (menu.value = null));
useEscapeLayer(editing, finishText);
useEscapeLayer(
  () => (current.value && !editing.value ? current.value.id : null),
  () => {
    selected.value = null;
    tool.value = null;
  },
);
useEscapeLayer(draft, () => {
  draft.value = null;
  tool.value = null;
});
useEscapeLayer(
  () => (tool.value && !draft.value && !current.value ? tool.value : null),
  () => (tool.value = null),
);

defineExpose({ finishText });
</script>

<template>
  <div class="image-editor" :aria-label="t('editor.label')">
    <header class="image-editor-bar">
      <div class="image-editor-history">
        <button
          type="button"
          class="image-editor-btn small"
          :aria-label="t('common.close')"
          v-tooltip="t('common.close')"
          @click="emit('close')"
        >
          <Icon name="wa-close" :size="20" />
        </button>
        <button
          type="button"
          class="image-editor-btn small"
          :disabled="!canUndo"
          :aria-label="t('editor.undo')"
          v-tooltip="t('editor.undo')"
          @click="undo"
        >
          <Icon name="wa-undo" :size="20" />
        </button>
        <button
          type="button"
          class="image-editor-btn small"
          :disabled="!canRedo"
          :aria-label="t('editor.redo')"
          v-tooltip="t('editor.redo')"
          @click="redo"
        >
          <Icon name="wa-redo" :size="20" />
        </button>
      </div>
      <div
        class="image-editor-tools"
        role="toolbar"
        :aria-label="t('editor.label')"
      >
        <span v-for="item in tools" :key="item.id" class="image-editor-anchor">
          <button
            type="button"
            class="image-editor-btn tool"
            :class="{ active: toolActive(item.id) }"
            :aria-label="t(item.label)"
            :aria-pressed="toolActive(item.id)"
            v-tooltip="t(item.label)"
            @click="onTool(item.id)"
          >
            <Icon :name="item.icon" :size="24" />
          </button>
          <Transition name="pop">
            <div
              v-if="item.id === 'shape' && menu === 'shapes'"
              class="image-editor-popover shapes"
              role="menu"
            >
              <button
                v-for="s in shapeKinds"
                :key="s.id"
                type="button"
                role="menuitem"
                :aria-label="t(`editor.shape.${s.id}`)"
                @click="addShape(s.id)"
              >
                <Icon :name="s.icon" :size="40" />
              </button>
            </div>
            <div
              v-else-if="item.id === 'emoji' && menu === 'emoji'"
              class="image-editor-popover emoji"
            >
              <EmojiPicker
                emoji-only
                autofocus
                @choose="addEmoji"
                @close="menu = null"
              />
            </div>
            <div
              v-else-if="item.id === 'sticker' && menu === 'sticker'"
              class="image-editor-popover stickers"
            >
              <strong>{{ t("editor.stickers_recent") }}</strong>
              <div
                v-if="recent && recent.length"
                class="image-editor-sticker-grid"
              >
                <button
                  v-for="s in recent"
                  :key="s.key"
                  type="button"
                  :aria-label="t('editor.sticker')"
                  @click="addSticker(s.url)"
                >
                  <img :src="s.url" alt="" />
                </button>
              </div>
              <p v-else-if="recent" class="muted small">
                {{ t("editor.stickers_none") }}
              </p>
              <span v-else class="spinner small" aria-hidden="true" />
              <button
                type="button"
                class="wa-button secondary compact"
                @click="stickerInput?.click()"
              >
                {{ t("editor.sticker_file") }}
              </button>
              <input
                ref="stickerInput"
                hidden
                type="file"
                accept="image/png,image/webp,image/jpeg,image/gif"
                @change="stickerFile"
              />
            </div>
          </Transition>
        </span>
        <span class="image-editor-anchor">
          <button
            type="button"
            class="image-editor-btn tool"
            :class="{ active: menu === 'hd', on: session.quality === 'hd' }"
            :aria-disabled="!hd"
            :aria-label="hd ? t('editor.quality') : t('editor.quality_none')"
            v-tooltip="hd ? t('editor.quality') : t('editor.quality_none')"
            @click="openQuality"
          >
            <Icon name="wa-hd" :size="24" />
          </button>
          <Transition name="pop">
            <div
              v-if="menu === 'hd'"
              class="image-editor-popover quality"
              role="menu"
            >
              <button
                type="button"
                role="menuitemradio"
                :aria-checked="session.quality === 'standard'"
                @click="setQuality('standard')"
              >
                <span>
                  {{ t("editor.quality_standard") }}
                  <small
                    >{{
                      standardSize !== null
                        ? formatFileSize(standardSize, locale) + " · "
                        : ""
                    }}{{ standardDims.w }} x {{ standardDims.h }}</small
                  >
                </span>
                <Icon
                  v-if="session.quality === 'standard'"
                  name="wa-check"
                  :size="20"
                />
              </button>
              <button
                type="button"
                role="menuitemradio"
                :aria-checked="session.quality === 'hd'"
                @click="setQuality('hd')"
              >
                <span>{{ t("editor.quality_hd") }}</span>
                <Icon
                  v-if="session.quality === 'hd'"
                  name="wa-check"
                  :size="20"
                />
              </button>
              <p>{{ t("editor.quality_help") }}</p>
            </div>
          </Transition>
        </span>
        <button
          v-if="draft"
          type="button"
          class="image-editor-ok"
          @click="applyCrop"
        >
          {{ t("editor.ok") }}
        </button>
        <button
          type="button"
          class="image-editor-btn tool"
          :aria-label="t('editor.copy')"
          v-tooltip="t('editor.copy')"
          @click="copy"
        >
          <Icon name="wa-copy" :size="24" />
        </button>
        <button
          type="button"
          class="image-editor-btn tool"
          :aria-label="t('editor.download')"
          v-tooltip="t('editor.download')"
          @click="download"
        >
          <Icon name="wa-download" :size="24" />
        </button>
      </div>
    </header>

    <div
      v-if="tool === 'filter'"
      class="image-editor-filters"
      role="radiogroup"
      :aria-label="t('editor.filter')"
    >
      <button
        v-for="f in FILTERS"
        :key="f"
        type="button"
        role="radio"
        class="image-editor-filter"
        :class="{ active: edit.filter === f }"
        :aria-checked="edit.filter === f"
        @click="setFilter(f)"
      >
        <span class="image-editor-filter-thumb">
          <img v-if="thumbs[f]" :src="thumbs[f]" alt="" />
          <span v-if="edit.filter === f" class="image-editor-filter-check"
            ><Icon name="wa-check" :size="24"
          /></span>
        </span>
        <span>{{ t(`editor.filter_name.${f}`) }}</span>
      </button>
    </div>

    <div ref="stage" class="image-editor-stage">
      <div
        class="image-editor-photo"
        :class="{
          drawing: tool === 'draw' || tool === 'blur',
          cropping: !!draft,
        }"
        :style="photoStyle"
        @pointerdown="onPhotoDown"
        @pointermove="onPhotoMove"
        @pointerup="onPhotoUp"
        @pointercancel="onPhotoUp"
        @dblclick="onPhotoDbl"
      >
        <canvas ref="canvas" />
        <div
          v-if="frameStyle"
          class="image-editor-frame"
          :style="frameStyle"
          @pointermove="onHandleMove"
          @pointerup="onHandleUp"
          @pointercancel="onHandleUp"
        >
          <span
            v-for="h in handles"
            :key="h"
            :class="['image-editor-handle', h]"
            @pointerdown="onHandleDown($event, h)"
          />
          <span
            class="image-editor-handle rotate"
            :aria-label="t('editor.rotate_item')"
            @pointerdown="onHandleDown($event, 'rotate')"
          />
        </div>
        <div
          v-if="textStyle"
          ref="textInput"
          class="image-editor-text"
          contenteditable="plaintext-only"
          role="textbox"
          :aria-label="t('editor.text_label')"
          :style="textStyle"
          @input="typing"
          @pointerdown.stop
          @blur="finishText"
        />
        <template v-if="cropBox">
          <span
            v-for="(d, i) in dims"
            :key="i"
            class="image-editor-dim"
            :style="d"
          />
          <div
            class="image-editor-crop"
            :style="{
              left: cropBox.left + 'px',
              top: cropBox.top + 'px',
              width: cropBox.width + 'px',
              height: cropBox.height + 'px',
            }"
            @pointerdown="onCropDown($event, 'move')"
            @pointermove="onCropMove"
            @pointerup="onCropUp"
            @pointercancel="onCropUp"
          >
            <span
              v-for="h in [
                'nw',
                'n',
                'ne',
                'e',
                'se',
                's',
                'sw',
                'w',
              ] as Handle[]"
              :key="h"
              :class="['image-editor-handle', h]"
              @pointerdown="onCropDown($event, h)"
            />
          </div>
        </template>
      </div>
    </div>

    <footer v-if="draft" class="image-editor-options">
      <button
        type="button"
        class="image-editor-btn tool"
        :aria-label="t('editor.rotate_left')"
        v-tooltip="t('editor.rotate_left')"
        @click="rotate(-90)"
      >
        <Icon name="wa-rotate-left" :size="24" />
      </button>
      <button
        type="button"
        class="image-editor-btn tool"
        :aria-label="t('editor.rotate_right')"
        v-tooltip="t('editor.rotate_right')"
        @click="rotate(90)"
      >
        <Icon name="wa-rotate-right" :size="24" />
      </button>
      <button
        type="button"
        class="image-editor-reset"
        :disabled="!draftChanged"
        @click="resetCrop"
      >
        {{ t("editor.reset") }}
      </button>
    </footer>
    <footer v-else-if="tool === 'draw'" class="image-editor-options">
      <EditorColors v-model="drawColor" />
      <span class="image-editor-sizes">
        <button
          v-for="i in brushSizes"
          :key="i"
          type="button"
          :aria-label="t('editor.brush_size', { size: i + 1 })"
          :aria-pressed="brush === i"
          @click="brush = i"
        >
          <span
            :style="{ width: 8 + i * 5 + 'px', height: 8 + i * 5 + 'px' }"
          />
        </button>
      </span>
    </footer>
    <footer v-else-if="tool === 'blur'" class="image-editor-options">
      <span class="image-editor-anchor">
        <button
          type="button"
          class="image-editor-menu-btn"
          :aria-label="t('editor.blur_mode')"
          :aria-expanded="menu === 'blurMode'"
          @click="menu = menu === 'blurMode' ? null : 'blurMode'"
        >
          <Icon
            :name="blurMode === 'pixelate' ? 'wa-pixelate' : 'wa-blur'"
            :size="24"
          />
          <Icon
            name="wa-chevron"
            :size="24"
            class="image-editor-caret"
            :class="{ open: menu === 'blurMode' }"
          />
        </button>
        <Transition name="pop">
          <div
            v-if="menu === 'blurMode'"
            class="image-editor-popover up modes"
            role="menu"
          >
            <button
              type="button"
              role="menuitemradio"
              :aria-checked="blurMode === 'pixelate'"
              :aria-label="t('editor.blur_pixelate')"
              @click="
                blurMode = 'pixelate';
                menu = null;
              "
            >
              <Icon name="wa-pixelate" :size="24" /><Icon
                v-if="blurMode === 'pixelate'"
                name="wa-check"
                :size="20"
              />
            </button>
            <button
              type="button"
              role="menuitemradio"
              :aria-checked="blurMode === 'blur'"
              :aria-label="t('editor.blur_soft')"
              @click="
                blurMode = 'blur';
                menu = null;
              "
            >
              <Icon name="wa-blur" :size="24" /><Icon
                v-if="blurMode === 'blur'"
                name="wa-check"
                :size="20"
              />
            </button>
          </div>
        </Transition>
      </span>
      <input
        v-model.number="blurSize"
        class="image-editor-range"
        type="range"
        :min="BLUR_SIZE.min"
        :max="BLUR_SIZE.max"
        :aria-label="t('editor.blur_size')"
      />
      <span class="image-editor-range-value">{{ blurSize }}</span>
      <button
        type="button"
        class="image-editor-btn tool"
        :aria-label="t('editor.delete')"
        v-tooltip="t('editor.delete')"
        @click="clearBlur"
      >
        <Icon name="wa-delete" :size="24" />
      </button>
    </footer>
    <footer v-else-if="current?.kind === 'text'" class="image-editor-options">
      <EditorColors
        :model-value="current.color"
        @update:model-value="
          (c) => c && updateItem((i) => i.kind === 'text' && (i.color = c))
        "
      />
      <span class="image-editor-anchor">
        <button
          type="button"
          class="image-editor-menu-btn font"
          :aria-label="t('editor.font')"
          :aria-expanded="menu === 'font'"
          @click="menu = menu === 'font' ? null : 'font'"
        >
          <span class="image-editor-font-badge">A</span
          >{{ fontOf(current.font).label }}
          <Icon
            name="wa-chevron"
            :size="24"
            class="image-editor-caret"
            :class="{ open: menu === 'font' }"
          />
        </button>
        <Transition name="pop">
          <div
            v-if="menu === 'font'"
            class="image-editor-popover up fonts"
            role="menu"
          >
            <button
              v-for="f in FONTS"
              :key="f.id"
              type="button"
              role="menuitemradio"
              :aria-checked="current.font === f.id"
              :style="{ fontFamily: f.family, fontWeight: f.weight }"
              @click="
                updateItem((i) => i.kind === 'text' && (i.font = f.id));
                menu = null;
              "
            >
              <span>{{ f.label }}</span
              ><Icon v-if="current.font === f.id" name="wa-check" :size="20" />
            </button>
            <span class="image-editor-aligns">
              <button
                v-for="a in ['left', 'center', 'right'] as const"
                :key="a"
                type="button"
                :aria-label="t(`editor.align_${a}`)"
                :aria-pressed="current.align === a"
                @click="updateItem((i) => i.kind === 'text' && (i.align = a))"
              >
                <Icon :name="`wa-align-${a}`" :size="24" />
              </button>
            </span>
          </div>
        </Transition>
      </span>
      <button
        type="button"
        class="image-editor-menu-btn"
        :aria-pressed="current.background"
        @click="
          updateItem((i) => i.kind === 'text' && (i.background = !i.background))
        "
      >
        <span class="image-editor-check" :class="{ on: current.background }"
          ><Icon v-if="current.background" name="wa-check" :size="16" /></span
        >{{ t("editor.background") }}
      </button>
      <button
        type="button"
        class="image-editor-btn tool"
        :aria-label="t('editor.delete')"
        v-tooltip="t('editor.delete')"
        @click="removeSelected"
      >
        <Icon name="wa-delete" :size="24" />
      </button>
    </footer>
    <footer v-else-if="current?.kind === 'shape'" class="image-editor-options">
      <span class="image-editor-anchor">
        <button
          type="button"
          class="image-editor-menu-btn"
          :aria-label="t('editor.fill')"
          :aria-expanded="menu === 'fill'"
          @click="menu = menu === 'fill' ? null : 'fill'"
        >
          <Icon
            name="wa-chevron"
            :size="24"
            class="image-editor-caret"
            :class="{ open: menu === 'fill' }"
          />
          <span
            class="image-editor-swatch fill"
            :style="{ background: current.fill ?? 'transparent' }"
          />
        </button>
        <Transition name="pop">
          <div v-if="menu === 'fill'" class="image-editor-popover up palette">
            <EditorColors
              allow-none
              compact
              :model-value="current.fill"
              @update:model-value="
                (c) => updateItem((i) => i.kind === 'shape' && (i.fill = c))
              "
            />
          </div>
        </Transition>
      </span>
      <span class="image-editor-anchor">
        <button
          type="button"
          class="image-editor-menu-btn"
          :aria-label="t('editor.stroke')"
          :aria-expanded="menu === 'stroke'"
          @click="menu = menu === 'stroke' ? null : 'stroke'"
        >
          <span
            class="image-editor-swatch ring"
            :style="{ borderColor: current.stroke ?? 'transparent' }"
          />
          <Icon
            name="wa-chevron"
            :size="24"
            class="image-editor-caret"
            :class="{ open: menu === 'stroke' }"
          />
        </button>
        <Transition name="pop">
          <div v-if="menu === 'stroke'" class="image-editor-popover up palette">
            <EditorColors
              allow-none
              compact
              :model-value="current.stroke"
              @update:model-value="
                (c) => updateItem((i) => i.kind === 'shape' && (i.stroke = c))
              "
            />
            <input
              class="image-editor-range wedge"
              type="range"
              :min="SHAPE_WIDTH.min"
              :max="SHAPE_WIDTH.max"
              :value="Math.round(current.width / unit)"
              :aria-label="t('editor.stroke_width')"
              @change="
                (e) =>
                  updateItem(
                    (i) =>
                      i.kind === 'shape' &&
                      (i.width =
                        Number((e.target as HTMLInputElement).value) * unit),
                  )
              "
            />
          </div>
        </Transition>
      </span>
      <button
        type="button"
        class="image-editor-btn tool"
        :aria-label="t('editor.delete')"
        v-tooltip="t('editor.delete')"
        @click="removeSelected"
      >
        <Icon name="wa-delete" :size="24" />
      </button>
    </footer>
    <footer v-else-if="current" class="image-editor-options">
      <button
        type="button"
        class="image-editor-btn tool"
        :aria-label="t('editor.delete')"
        v-tooltip="t('editor.delete')"
        @click="removeSelected"
      >
        <Icon name="wa-delete" :size="24" />
      </button>
    </footer>
  </div>
</template>
